import React, { createContext, useContext, useMemo } from 'react';
import { NotificationEntityType } from 'types';
import { useNotificationEntityUserFavoritesApi } from 'app/shared/hooks/useNotificationEntityUserFavoritesApi';

export interface INotificationEntityFavoritesContextValue {
  enabled: boolean;
  favoriteIds: readonly string[];
  isFavorite: (internalId: string) => boolean;
  toggleFavorite: (internalId: string) => Promise<void>;
  isSavingFavorite: boolean;
}

const defaultValue: INotificationEntityFavoritesContextValue = {
  enabled: false,
  favoriteIds: [],
  isFavorite: () => false,
  toggleFavorite: async () => undefined,
  isSavingFavorite: false,
};

const NotificationEntityFavoritesContext =
  createContext<INotificationEntityFavoritesContextValue>(defaultValue);

export function NotificationEntityFavoritesProvider({
  organizationId,
  entityType,
  children,
}: {
  organizationId: string;
  entityType: NotificationEntityType;
  children: React.ReactNode;
}) {
  const enabled = !!organizationId;

  const { favoriteIds, isFavorite, toggleFavorite, isSavingFavorites } =
    useNotificationEntityUserFavoritesApi({
      organizationId,
      entityType,
      enabled,
    });

  const value = useMemo<INotificationEntityFavoritesContextValue>(
    () => ({
      enabled,
      favoriteIds,
      isFavorite,
      toggleFavorite,
      isSavingFavorite: isSavingFavorites,
    }),
    [enabled, favoriteIds, isFavorite, isSavingFavorites, toggleFavorite],
  );

  return (
    <NotificationEntityFavoritesContext.Provider value={value}>
      {children}
    </NotificationEntityFavoritesContext.Provider>
  );
}

export function useNotificationEntityFavorites() {
  return useContext(NotificationEntityFavoritesContext);
}
